import type { ColorScheme as PublicColorScheme } from '../../../showDiffPluginType';

import type { ColorScheme, DiffAccentColor, DiffColorScheme } from './types';

/** Green insertions, red deletions. */
export const traditionalScheme: DiffColorScheme = {
	insertColor: 'green',
	insertUnderlineStyle: 'solid',
	insertActiveColor: 'green',
	deleteColor: 'red',
	deleteActiveColor: 'red',
	deletedCellColor: 'gray',
	deletedCellOpacity: 0.5,
	deletedLozengeColor: 'gray',
	deletedLozengeTextTone: 'inverse',
	deletedMediaRingColor: 'red',
	deletedRowTreatment: 'strikeColor',
	insertedCellOpacity: 0.5,
	deletedQuoteNodeShape: 'ring',
	deletedCellBorderProminence: 'subtle',
	deletedBlockOutlineActiveEmphasis: 'background',
	addedCellOverlayZIndex: 1,
	roundedAddedCellOverlayInheritsBorder: true,
	insertedInlineTreatment: 'underline',
	deletedInlineTreatment: 'strikethrough',
	insertedNodeEmphasis: 'stateful',
	deletedNodeEmphasis: 'stateful',
	deletedQuoteNodeActiveTone: 'backgroundPressed',
	strikesDeletedEmbedCard: true,
};

/** Purple insertions, gray deletions (red on active). */
export const standardScheme: DiffColorScheme = {
	insertColor: 'purple',
	insertUnderlineStyle: 'dotted',
	insertActiveColor: 'purple',
	deleteColor: 'gray',
	deleteActiveColor: 'red',
	deletedCellColor: 'gray',
	deletedCellOpacity: 0.5,
	deletedLozengeColor: 'gray',
	deletedLozengeTextTone: 'inverse',
	// Red, not `deleteColor`'s gray: a11y-fixes rings deleted media red in both schemes.
	deletedMediaRingColor: 'red',
	deletedRowTreatment: 'textTint',
	insertedCellOpacity: 0.2,
	deletedQuoteNodeShape: 'borderLeft',
	deletedCellBorderProminence: 'accent',
	deletedBlockOutlineActiveEmphasis: 'border',
	addedCellOverlayZIndex: 2,
	roundedAddedCellOverlayInheritsBorder: false,
	insertedInlineTreatment: 'borderBottom',
	deletedInlineTreatment: 'glyphTint',
	insertedNodeEmphasis: 'static',
	deletedNodeEmphasis: 'static',
	deletedQuoteNodeActiveTone: 'borderAccent',
	strikesDeletedEmbedCard: false,
};

const createAttributionScheme = (color: DiffAccentColor): DiffColorScheme => ({
	...standardScheme,
	insertColor: color,
	insertActiveColor: color,
	deleteColor: color,
	deleteActiveColor: color,
	deletedCellColor: color,
});

/** Maps public schemes and attribution-only accents to their data objects. */
export const colorSchemeRegistry: Record<ColorScheme, DiffColorScheme> = {
	blue: createAttributionScheme('blue'),
	green: createAttributionScheme('green'),
	magenta: createAttributionScheme('magenta'),
	orange: createAttributionScheme('orange'),
	purple: createAttributionScheme('purple'),
	teal: createAttributionScheme('teal'),
	traditional: traditionalScheme,
	standard: standardScheme,
};

/** Attribution accents are impossible in the refactor-off cohort; fall back defensively. */
export const getLegacyColorScheme = (
	colorScheme: ColorScheme | undefined,
): PublicColorScheme | undefined =>
	colorScheme === 'standard' || colorScheme === 'traditional' ? colorScheme : undefined;
